import axios from 'axios';
import authService from './authService';
import { API_URL } from '@/config/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = authService.token.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
    }
    return Promise.reject(error);
  }
);

const estudoService = {
  // ========== TEMAS DE ESTUDO ==========

  // Criar novo tema
  async criarTema(tema) {
    try {
      const response = await api.post('/temas-estudo', tema);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar tema:', error);
      throw error;
    }
  },

  // Listar todos os temas
  async listarTemas() {
    try {
      const response = await api.get('/temas-estudo');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar temas:', error);
      throw error;
    }
  },

  // Listar apenas temas ativos
  async listarTemasAtivos() {
    try {
      const response = await api.get('/temas-estudo/ativos');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar temas ativos:', error);
      throw error;
    }
  },

  // Buscar tema por ID
  async buscarTema(id) {
    try {
      const response = await api.get(`/temas-estudo/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar tema:', error);
      throw error;
    }
  },

  // Atualizar tema
  async atualizarTema(id, tema) {
    try {
      const response = await api.put(`/temas-estudo/${id}`, tema);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar tema:', error);
      throw error;
    }
  },

  // Deletar (desativar) tema
  async deletarTema(id) {
    try {
      await api.delete(`/temas-estudo/${id}`);
      return true;
    } catch (error) {
      console.error('Erro ao deletar tema:', error);
      throw error;
    }
  },

  // ========== SESSÕES DE ESTUDO ==========

  // Iniciar nova sessão
  async iniciarSessao(temaEstudoId, observacao = '') {
    try {
      const response = await api.post('/sessoes-estudo/iniciar', {
        temaEstudoId,
        observacao
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao iniciar sessão:', error);
      throw error;
    }
  },

  // Pausar sessão
  async pausarSessao(id) {
    try {
      const response = await api.put(`/sessoes-estudo/${id}/pausar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao pausar sessão:', error);
      throw error;
    }
  },

  // Retomar sessão
  async retomarSessao(id) {
    try {
      const response = await api.put(`/sessoes-estudo/${id}/retomar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao retomar sessão:', error);
      throw error;
    }
  },

  // Finalizar sessão
  async finalizarSessao(id) {
    try {
      const response = await api.put(`/sessoes-estudo/${id}/finalizar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao finalizar sessão:', error);
      throw error;
    }
  },

  // Buscar sessão por ID
  async buscarSessao(id) {
    try {
      const response = await api.get(`/sessoes-estudo/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar sessão:', error);
      throw error;
    }
  },

  // Listar últimas sessões de um tema
  async listarSessoesTema(temaId) {
    try {
      const response = await api.get(`/sessoes-estudo/tema/${temaId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar sessões do tema:', error);
      throw error;
    }
  },

  // Obter progresso semanal (todos os temas)
  async obterProgressoSemanal() {
    try {
      const response = await api.get('/sessoes-estudo/progresso-semanal');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter progresso semanal:', error);
      throw error;
    }
  },

  // Obter progresso semanal de um tema especfico
  async obterProgressoSemanalTema(temaId) {
    try {
      const response = await api.get(`/sessoes-estudo/progresso-semanal/${temaId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter progresso semanal do tema:', error);
      throw error;
    }
  },

  // Obter progresso semanal por data específica (formato yyyy-MM-dd)
  async obterProgressoSemanalPorData(data) {
    try {
      const response = await api.get(`/sessoes-estudo/progresso-semanal/data/${data}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter progresso semanal por data:', error);
      throw error;
    }
  },

  // Inserir sesso manual (já finalizada)
  async inserirSessaoManual(dados) {
    try {
      const response = await api.post('/sessoes-estudo/manual', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao inserir sessão manual:', error);
      throw error;
    }
  },

  // Cancelar sesso pausada
  async cancelarSessao(id) {
    try {
      await api.delete(`/sessoes-estudo/${id}/cancelar`);
      return true;
    } catch (error) {
      console.error('Erro ao cancelar sessão:', error);
      throw error;
    }
  },

  // Obter sala de estudos (quem está estudando junto)
  async obterSalaEstudos() {
    try {
      const response = await api.get('/sessoes-estudo/sala-estudos');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter sala de estudos:', error);
      throw error;
    }
  }
};

export default estudoService;

